import _ from 'lodash';
import axios from 'axios';

import { showNotification } from 'services/api/notifications';
import {
  ApiConfig,
  SnackBarConfig,
  SnackBarConfigWithMessages,
  SimpleSnackBarConfig,
} from './types';
import { Pagination } from 'services/search';
import { PaginationDirection } from 'services/url';

import { api, csvApi } from './config';
import { methodDisplayNameMapper } from './consts';

const displayedSuccessMessages = new Set<string>();
const displayedErrorMessages = new Set<string>();

/**
 * Function which creates an API call for given api config and associated action types
 */
export const createApiCall =
  (apiConfig: ApiConfig, snackbarConfig?: SnackBarConfig) => async () => {
    const hostname = apiConfig.hostname || api.hostname;
    const path = apiConfig.path;
    const method = _.get(apiConfig, 'method', 'GET');
    let NOTIFICATION_CACHE_TIMEOUT_MS = 60 * 1000;
    if (apiConfig.timeout) {
      NOTIFICATION_CACHE_TIMEOUT_MS = apiConfig.timeout;
    }
    const contentType =
      _.includes(['POST', 'PUT', 'PATCH'], method) && !apiConfig.formData
        ? {
            'Content-Type': 'application/json',
          }
        : {};
    const passedHeaders = _.get(apiConfig, 'headers', {});
    const headers = {
      ...contentType,
      ...passedHeaders,
    };
    const axiosConfig = {
      method,
      headers,
      url: `${hostname}${path}`,
      timeout: NOTIFICATION_CACHE_TIMEOUT_MS,
      data: apiConfig.body,
    };

    // if there is no snackbar return simple axios call
    if (!snackbarConfig) {
      return axios(axiosConfig);
    }

    // Handle snackbarConfig by checking its type
    let getMessageHandlers:
      | SnackBarConfigWithMessages['getMessage']
      | undefined;
    let simpleServiceName: SimpleSnackBarConfig['serviceName'] | undefined;
    let suppressErrorToast = false; // Default to not suppressing

    if ('getMessage' in snackbarConfig) {
      // Type guard for SnackBarConfigWithMessages
      getMessageHandlers = snackbarConfig.getMessage;
      suppressErrorToast = snackbarConfig.suppressErrorNotification === true;
    } else if ('serviceName' in snackbarConfig) {
      // Type guard for SimpleSnackBarConfig
      simpleServiceName = snackbarConfig.serviceName;
      // suppressErrorNotification is not part of SimpleSnackBarConfig, so suppressErrorToast remains false
    }

    try {
      const response = await axios(axiosConfig);

      // show success snack bar
      const methodDisplayName = methodDisplayNameMapper[method.toLowerCase()];
      const successMessage =
        getMessageHandlers?.success?.(response) ||
        (simpleServiceName
          ? `${simpleServiceName} successfully ${methodDisplayName}`
          : undefined);

      if (successMessage) {
        if (
          typeof document === 'undefined' ||
          !displayedSuccessMessages.has(successMessage)
        ) {
          showNotification(successMessage, { variant: 'success' });
          if (typeof document !== 'undefined') {
            displayedSuccessMessages.add(successMessage);
            // Remove the message from the set after a delay (e.g., 5 seconds)
            // to allow it to be shown again later if the same action is repeated.
            setTimeout(() => {
              displayedSuccessMessages.delete(successMessage);
            }, NOTIFICATION_CACHE_TIMEOUT_MS);
          }
        }
      }

      return response;
    } catch (error) {
      // show error snack bar
      if (suppressErrorToast !== true) {
        const errorMessage =
          getMessageHandlers?.error?.(error) ||
          (simpleServiceName
            ? `${simpleServiceName} encountered an error. Please try again`
            : 'An error occurred. Please try again');

        if (errorMessage) {
          if (
            typeof document === 'undefined' ||
            !displayedErrorMessages.has(errorMessage)
          ) {
            showNotification(errorMessage, { variant: 'error' });
            if (typeof document !== 'undefined') {
              displayedErrorMessages.add(errorMessage);
              // Remove the message from the set after a delay (e.g., 5 seconds)
              // to allow it to be shown again later if the same error is repeated.
              setTimeout(() => {
                displayedErrorMessages.delete(errorMessage);
              }, NOTIFICATION_CACHE_TIMEOUT_MS);
            }
          }
        }
      }
      throw error;
    }
  };

export const paginatedApiCall = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T,
  skipPagination: boolean = false
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `orderBy=${pagination.sort.sortBy}`,
    `ascending=${pagination.sort.direction === PaginationDirection.Ascending}`,
    `pageSize=${pagination.pageSize}`,
    `pageNumber=${pagination.page}`,
  ];

  let url = `${api.hostname}${path}${
    path.includes('?') ? '&' : '?'
  }${paginationParts.join('&')}`;
  if (skipPagination) {
    url = `${api.hostname}${path}`;
  }

  const { data } = await axios.get(url);
  if (skipPagination) {
    return {
      data: transformation ? data.data.map(transformation) : data.results,
      pagination: { ...pagination, totalRows: data.maxResults },
    };
  }

  return {
    data: transformation ? data.results.map(transformation) : data.results,
    pagination: { ...pagination, totalRows: data.maxResults },
  };
};

export const paginatedCSVApiCall = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const url = `${csvApi.hostname}${path}`;

  const { data } = await axios.get(url);

  return {
    data: transformation ? data.data.map(transformation) : data.data,
    pagination: { ...pagination, totalRows: data.maxResults },
  };
};

export const paginatedApiCallZeroBased = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `orderBy=${pagination.sort.sortBy}`,
    `ascending=${pagination.sort.direction === PaginationDirection.Ascending}`,
    `pageSize=${pagination.pageSize}`,
    `pageNumber=${(pagination?.page || 1) - 1}`,
  ];

  const url = `${api.hostname}${path}${
    path.includes('?') ? '&' : '?'
  }${paginationParts.join('&')}`;

  const { data } = await axios.get(url);

  return {
    data: transformation ? data.results.map(transformation) : data.results,
    pagination: { ...pagination, totalRows: data.maxResults },
  };
};

export const paginatedApiCallNewStandard = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `order.by=${pagination.sort.sortBy}`,
    `order.direction=${
      pagination.sort.direction === PaginationDirection.Ascending
        ? 'ASC'
        : 'DESC'
    }`,
    `page.size=${pagination.pageSize}`,
    `page.number=${(pagination?.page || 1) - 1}`,
  ];

  const url = `${api.hostname}${path}${
    path.includes('?') ? '&' : '?'
  }${paginationParts.join('&')}`;

  const { data } = await axios.get(url);
  return {
    data: transformation ? data.results.map(transformation) : data.results,
    pagination: { ...pagination, totalRows: data.maxResults },
  };
};
export const paginatedApiCallNewStandard2 = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `order.by=${pagination.sort.sortBy}`,
    `order.direction=${
      pagination.sort.direction === PaginationDirection.Ascending
        ? 'ASC'
        : 'DESC'
    }`,
    `page.size=${pagination.pageSize}`,
    `page.number=${(pagination?.page || 1) - 1}`,
  ];

  const url = `${api.hostname}${path}${
    path.includes('?') ? '&' : '?'
  }${paginationParts.join('&')}`;

  const { data } = await axios.get(url);

  return {
    data: transformation ? data.data.map(transformation) : data.data,
    pagination: {
      ...pagination,
      totalRows: data.pagination.totalItems,
    },
  };
};

export const paginatedApiCallNewStandard3 = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `orderBy=${pagination.sort.sortBy}`,
    `ascending=${pagination.sort.direction === PaginationDirection.Ascending}`,
    `pageSize=${pagination.pageSize}`,
    `pageNumber=${(pagination?.page || 1) - 1}`,
  ];

  const url = `${api.hostname}${path}${
    path.includes('?') ? '&' : '?'
  }${paginationParts.join('&')}`;

  const { data } = await axios.get(url);

  return {
    data: transformation ? data.data.map(transformation) : data.data,
    pagination: {
      ...pagination,
      totalRows: data.pagination.totalItems,
    },
  };
};
